"use client";

import type {
  StripeCustomerID,
  StripeSubscriptionID,
} from "@repo/db/branded-types";
import { useRouter } from "next/navigation";
import { useExpressCheckoutRedirect } from "src/lib/use-express-checkout-redirect";
import { usePaymentComplete } from "src/lib/use-payment-complete";
import { Routes } from "src/lib/routes";

/**
 * Paywall / express-checkout completion wiring shared by both home wizards.
 *
 * `onComplete` runs on a successful inline checkout AND on an express-checkout
 * (Apple/Google Pay) redirect success — both mean "subscription is active,
 * leave the paywall." Each wizard passes
 * `() => dispatch({ type: "PAYWALL_COMPLETE" })`; the dispatch stays in the
 * caller because the two wizards' reducer action types differ.
 */
export function usePaywallCompletion(onComplete: () => void) {
  const router = useRouter();
  const processPayment = usePaymentComplete();

  const { phase: expressPhase } = useExpressCheckoutRedirect({
    onPaymentSuccess: onComplete,
  });

  const handleCheckoutComplete = async (data: {
    customerId: StripeCustomerID;
    subscriptionId: StripeSubscriptionID;
  }) => {
    try {
      await processPayment(data);
      onComplete();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Payment processing failed";
      router.push(`${Routes.ERROR}?message=${encodeURIComponent(message)}`);
    }
  };

  const handlePaymentError = (message: string) => {
    router.push(`${Routes.ERROR}?message=${encodeURIComponent(message)}`);
  };

  return { expressPhase, handleCheckoutComplete, handlePaymentError };
}
